import { GUI } from "dat.gui";
import {
    BoundingBoxAA,
    CreateRopePositionsTexture,
    GBasePass,
    GetBasePassUniformParametersList,
    Mesh,
} from "../../../basePass";
import { GRenderTargets } from "../../../core/renderTargets";
import { BindRenderTarget } from "../../../core/resourcesUtils";
import { GScreenDesc } from "../../../core/screenDesc";
import { ParticlesEmitter } from "../../../particles";
import { GetDustParticlesDesc } from "../../../particlesConfig";
import { SceneBase } from "../../scene";
import { GetVec2, GetVec3, GStack, Vector3 } from "../../../core/types";
import { GTexturePool } from "../../../core/texturePool";
import { SDFRender } from "../../sdf";
import { GSettings } from "../../../core/settings";
import { VectorField } from "../../../vectorField";
import {
    GetShaderSourceBasePassAlphaRenderMainPS,
    GetShaderSourceBasePassDensityRenderMainPS,
    GetShaderSourceBasePassMainPS,
    GetShaderSourceBasePassMainVS,
} from "../../../shaders/shaderBasePass";
import { Shader } from "../../../core/shaderUtils";

import { InitSDFDesc, InitSDFDescPhoneScene, InitializeMeshes } from "./lightSwitchSceneDesc";
import {
    ESubSceneState,
    OnSubSceneStateTransitionUpdate,
    SetNewSubSceneState,
    TransitionToNextSubSceneState,
    OnScriptUpdateMeshesForCurSceneState,
    OnPhoneSceneUpdate,
    LightSwitchOnUpdateAdditional,
    SubSceneDesc,
} from "./lightSwitchScripts";
import {
    DensityRenderEventUpdate,
    InjectProjectorTextureToDensity,
    InjectionParticle,
} from "./lightSwitchDensityRender";
import { GTime } from "../../../core/utils";
import { SwitchRopeTool } from "./lightSwitchRopeTool";
import { GSimpleShapesRenderer } from "../../../helpers/shapeRender";
import { RTexture } from "../../../core/texture";
import { SpatialControlPointWS } from "../../../core/spatialController";
import { GAudioEngine, SoundSample } from "../../../core/audioEngine";

export class PayneSceneLightSwitch extends SceneBase {
    static GInstance: PayneSceneLightSwitch | null = null;

    //Base Pass Render Data
    SceneSDFDescPaintingScene: string | null = null;
    ShaderBasePassPhoneScene: Shader | null = null;
    SceneSDFDescPhoneScene: string | null = null;

    SDFSceneRender: SDFRender;
    pCurSDFMesh: Mesh;
    pCurSDFMesh2: Mesh | null;

    DustParticles;

    MeshWallFront = new Mesh();

    MeshPaintingFrameWood = new Mesh();
    MeshPaintingFrameMetal = new Mesh();
    MeshPainting = new Mesh();
    MeshBird = new Mesh();
    MeshEye = new Mesh();

    NicPaintingMesh: Mesh | null = null;
    HotlineMeshesArr: Mesh[] = [];
    MeshPhone = new Mesh();
    MeshPills = new Mesh();
    MeshPhoneHandle = new Mesh();
    MeshPhoneHandleSpatialController = new SpatialControlPointWS(GetVec3(2.0, 1.0, -4.0));

    MeshDestrPainting: Mesh[] = [];
    MeshLampRightArr: Mesh[] = [];
    MeshLampLeftArr: Mesh[] = [];
    MeshWingLeft!: Mesh;
    MeshWingRight!: Mesh;
    ShaderBasePassAlpha: Shader | null = null;

    //DestrHotlineCommonDesc
    LampLeftAABB = new BoundingBoxAA();
    LampRightAABB = new BoundingBoxAA();
    PaintingAABB = new BoundingBoxAA();
    PaintingFrameAABB = new BoundingBoxAA();

    FloorPlinthSize;
    CeilCorniceSize;

    PaintingTexturesArr: WebGLTexture[] = [];
    PaintingTextureCur: WebGLTexture | null = null;
    //PaintingTextureInitial: WebGLTexture | null = null;
    //PaintingTextureNext: WebGLTexture | null = null;

    ProjectorTexturesArr: WebGLTexture[] = [];
    ProjectorTexture = new RTexture();
    //ProjectorTextureNext: WebGLTexture | null = null;

    LoadPaintingAndProjectorTextures(gl: WebGL2RenderingContext) {
        for (let i = 1; i < ESubSceneState.Phone; i++) {
            const desc = SubSceneDesc.GetDescBasedOnState(i);

            this.PaintingTexturesArr[i - 1] = GTexturePool.CreateTexture(gl, false, desc.PaintTextureName!, true, true);
            this.ProjectorTexturesArr[i - 1] = GTexturePool.CreateTexture(gl, false, desc.ProjTextureName!, true, true);
        }
    }

    VectorFieldProjector;
    DispersionTexture: WebGLTexture | null = null;
    DispersionMaskTexture: WebGLTexture | null = null;
    ShaderBasePassDensity: Shader | null = null;

    CurSceneState = ESubSceneState.Rose;
    bRenderWings = false;

    //Sounds
    PhoneRingStartTime = 0.0;
    SoundRoomNoise: SoundSample = new SoundSample();
    RoomNoiseInitGain = 0.03;
    SoundHeartbeat: SoundSample = new SoundSample();
    SoundHeartbeat2: SoundSample = new SoundSample();
    SoundChain: SoundSample = new SoundSample();
    SoundPainkiller: SoundSample = new SoundSample();
    SoundChild: SoundSample = new SoundSample();
    SoundScream: SoundSample = new SoundSample();
    SoundAh: SoundSample = new SoundSample();
    SoundMona: SoundSample = new SoundSample();
    SoundFire: SoundSample = new SoundSample();
    SoundPhone: SoundSample = new SoundSample();
    SoundHotline: SoundSample = new SoundSample();
    SoundShotEcho: SoundSample = new SoundSample();

    async LoadSounds() {
        const audioEngine = GAudioEngine.Instance!;
        if (audioEngine.Context && audioEngine.MasterGain) {
            this.SoundRoomNoise.Init(
                audioEngine.Context,
                "roomNoise.mp3",
                audioEngine.MasterGain,
                this.RoomNoiseInitGain,
            );
            this.SoundHeartbeat.Init(audioEngine.Context, "heartbeat2.mp3", audioEngine.MasterGain, 1.3);
            this.SoundHeartbeat2.Init(audioEngine.Context, "heartbeat3.mp3", audioEngine.MasterGain, 1.3);
            this.SoundChild.Init(audioEngine.Context, "child3.mp3", audioEngine.MasterGain, 0.01);
            this.SoundScream.Init(audioEngine.Context, "scream2.mp3", audioEngine.MasterGain, 0.1);
            this.SoundAh.Init(audioEngine.Context, "ah.mp3", audioEngine.MasterGain, 0.1);
            this.SoundChain.Init(audioEngine.Context, "chain.mp3", audioEngine.MasterGain, 0.075);
            this.SoundPainkiller.Init(audioEngine.Context, "painkillers.mp3", audioEngine.MasterGain, 0.1);
            this.SoundMona.Init(audioEngine.Context, "mona2.mp3", audioEngine.MasterGain, 0.1);
            this.SoundShotEcho.Init(audioEngine.Context, "valerie.mp3", audioEngine.MasterGain, 0.2);
            this.SoundPhone.Init(audioEngine.Context, "phone.mp3", audioEngine.MasterGain, 0.2);
            this.SoundFire.Init(audioEngine.Context, "fireHigh.mp3", audioEngine.MasterGain, 1.0);
            this.SoundHotline.Init(audioEngine.Context, "hotline2.mp3", audioEngine.MasterGain, 1.0);
        }
    }

    PlayHeartbeat() {
        const audioContext = GAudioEngine.Instance!.Context;
        if (audioContext && this.CurSceneState < ESubSceneState.Nicole) {
            if (Math.random() < 0.5) {
                this.SoundHeartbeat.Play(audioContext);
            } else {
                this.SoundHeartbeat2.Play(audioContext);
            }
        }
    }

    PlayHeartbeatAddSound() {
        const audioContext = GAudioEngine.Instance!.Context;
        if (audioContext) {
            switch (this.CurSceneState) {
                case ESubSceneState.Rose:
                    //this.SoundChild.Play(audioContext);
                    break;
                case ESubSceneState.Michelle:
                    this.SoundChild.Play(audioContext, false, 0.0, audioContext.currentTime + 1.0);
                    break;
                case ESubSceneState.Mona:
                    this.SoundScream.Play(audioContext, false, 0.0, audioContext.currentTime + Math.random());
                    //this.SoundAh.Play(audioContext);
                    break;
                case ESubSceneState.Valerie:
                    this.SoundMona.Play(audioContext);
                    break;
                case ESubSceneState.Nicole:
                    this.SoundShotEcho.Play(audioContext, false, 0.0, audioContext.currentTime + 0.1);
                    break;
                case ESubSceneState.Phone:
                    break;
                default:
                    break;
            }
        }
    }

    SoundOnFireOn() {
        const audioContext = GAudioEngine.Instance!.Context;
        if (audioContext) {
            if (this.SoundFire.Buffer && !this.SoundFire.bIsPlaying) {
                const offset = Math.random() * this.SoundFire.Buffer.duration * 0.2;
                this.SoundFire.Play(audioContext, false, offset);
            }
        }
    }
    SoundOnFireOff() {
        if (this.SoundFire.bIsPlaying) {
            this.SoundFire.Stop();
        }
    }

    SubmitDebugUI(datGui: dat.GUI): GUI {
        const folder = super.SubmitDebugUI(datGui);

        /* {
            const meshRef = this.MeshWingRight;
            const name = 'Wing"';
            const meshFolder = folder.addFolder(name);
            DatGuiAddVec3(meshFolder, meshRef.Position, `Position`, -10, 10, false);
            DatGuiAddVec3(meshFolder, meshRef.Scale, `Scale`, 0.01, 20, false);
            DatGuiAddVec3(meshFolder, meshRef.Orientation, `Rotation`, -Math.PI, Math.PI, false);
            meshFolder.add(meshRef.Material, "Metalness", 0, 1).name("Metalness").step(0.01);
            meshFolder.add(meshRef.Material, "Brightness", 0, 5).name("Brightness").step(0.01).listen();
            meshFolder.add(meshRef.Material, "TextureScale", 0, 5).name("UVScale").step(0.01);
        } */

        folder.add(this, "bNewSubSceneStateThisFrame").listen();
        folder.add(this, "bUpdateRope").listen();
        folder.add(this, "RenderToDensityColorScale", 0.0, 1.0).step(0.01);
        folder.addColor(this, "DensityRenderColorHex").onChange(function () {
            //ConvertHexColorToVec3(hexColor, this.DensityRenderColor);
        });

        const physFolder = folder.addFolder("Rope");
        this.SwitchRope.Rope.SubmitDebugUI(physFolder);

        return folder;
    }

    //For Scripts:
    bNewSubSceneStateThisFrame = false;
    SubSceneStateTransitionParameterSec = 0.5;
    bLightAnimFinished = false;
    bProjectorBulletAnimFired = true;
    bProjectorSoundPlayed = true;
    TimeSinceFirstSceneRender = 0.0;
    bPhonePickedUp = false;
    bPhonePickedUpThisFrame = false;
    bScreamTextureRendered = false;
    PhonePickupTimeParam = 0.0;
    ScreamTextureRevealTime = Math.random() > 0.2 ? 0.0 : 3.0;
    ScreamTexture;

    //Density Render
    bRenderProjectorToDensity = true;
    bRenderColorToDensity = true;
    RenderToDensityColorScale = 1.0;
    DensityRenderColorHex = "#560000";
    DensityRenderColor = new Vector3(0, 0, 0);
    DensityRenderParticle = new InjectionParticle(0);
    DensityRenderParticleUp = new InjectionParticle(1);
    DensityRenderParticleDown = new InjectionParticle(-1);

    //Switch
    SwitchRope;

    bUpdateRope = false;

    constructor(gl: WebGL2RenderingContext) {
        super();

        {
            VectorField.GFullscreenInstance!.DensityDecay = 0.3;
            VectorField.GFullscreenInstance!.DensityAlphaDecay = 0.3;
            VectorField.GFullscreenInstance!.VelocityDecay = 2.0;
            VectorField.GFullscreenInstance!.VelocityDiffusion = 0.1;
            //VectorField.GFullscreenInstance!.VorticityScale = 15.1;
            const vSize = 256;
            const vfSize = GetVec2(vSize * 1.375, vSize);
            const dSize = 1024.0;
            const densSize = GetVec2(dSize * 1.375, dSize);
            this.VectorFieldProjector = new VectorField(gl, vfSize, densSize, false);

            this.DispersionTexture = GTexturePool.CreateTexture(gl, false, "dispersDens2", true, true, false);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.MIRRORED_REPEAT);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.MIRRORED_REPEAT);
            this.DispersionMaskTexture = GTexturePool.CreateTexture(gl, false, "densMask_R8", true, true, false);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.MIRRORED_REPEAT);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.MIRRORED_REPEAT);
        }

        CreateRopePositionsTexture(gl);

        SceneBase.UniformBuffer.Params.OccluderPlaneCenter.Set3(0, 0.85, -0.1);

        SceneBase.UniformBuffer.Params.SceneBoundsCenter.Set3(0, 0, -4);
        SceneBase.UniformBuffer.Params.SceneBoundsExtent.Set3(5.5, 4.5, 4);

        this.FloorPlinthSize = new Vector3(SceneBase.UniformBuffer.Params.SceneBoundsExtent.x * 2, 0.15, 0.02);
        this.CeilCorniceSize = new Vector3(SceneBase.UniformBuffer.Params.SceneBoundsExtent.x * 2, 0.07, 0.05);

        this.DustParticles = new ParticlesEmitter(gl, GetDustParticlesDesc());

        this.LoadPaintingAndProjectorTextures(gl);
        InitializeMeshes(gl, this);
        //InitializeHotlineMeshes(gl, this);

        const probeRadius = SceneBase.LightprobesManager.ProbeRadius;
        SceneBase.LightprobesManager.Init(
            gl,
            SceneBase.UniformBuffer.Params.SceneBoundsCenter,
            GetVec3(
                SceneBase.UniformBuffer.Params.SceneBoundsExtent.x - probeRadius,
                SceneBase.UniformBuffer.Params.SceneBoundsExtent.y - probeRadius,
                SceneBase.UniformBuffer.Params.SceneBoundsExtent.z - probeRadius,
            ),
        );

        //Define Specific Base Pass Render Program with analytic SDF
        this.SceneSDFDescPaintingScene = InitSDFDesc(this);
        this.ShaderBasePassCurScene = new Shader(
            gl,
            GetShaderSourceBasePassMainVS(),
            GetShaderSourceBasePassMainPS(this.SceneSDFDescPaintingScene),
            GetBasePassUniformParametersList,
        );
        this.ShaderBasePassAlpha = new Shader(
            gl,
            GetShaderSourceBasePassMainVS(),
            GetShaderSourceBasePassAlphaRenderMainPS(),
            GetBasePassUniformParametersList,
        );
        this.ShaderBasePassDensity = new Shader(
            gl,
            GetShaderSourceBasePassMainVS(),
            GetShaderSourceBasePassDensityRenderMainPS(VectorField.GFullscreenInstance!.DensityNumCells),
            GetBasePassUniformParametersList,
        );
        this.SDFSceneRender = new SDFRender(gl, this.SceneSDFDescPaintingScene);

        this.pCurSDFMesh = this.MeshBird;
        this.pCurSDFMesh2 = null;

        this.SceneSDFDescPhoneScene = InitSDFDescPhoneScene(this);
        this.ShaderBasePassPhoneScene = new Shader(
            gl,
            GetShaderSourceBasePassMainVS(),
            GetShaderSourceBasePassMainPS(this.SceneSDFDescPhoneScene),
            GetBasePassUniformParametersList,
        );

        this.SwitchRope = new SwitchRopeTool(
            gl,
            SceneBase.UniformBuffer.Params.SceneBoundsCenter,
            SceneBase.UniformBuffer.Params.SceneBoundsExtent,
            this,
        );

        this.ScreamTexture = GTexturePool.CreateTexture(gl, false, `smile_R8`);

        this.LoadSounds();

        //this.ForceSetSubSceneState(gl, ESubSceneState.Mona);
        SetNewSubSceneState(gl, ESubSceneState.Rose, this);

        //TransitionSceneToPhoneState(gl, this);
    }

    //=============================================================================================================================
    //
    // 														RENDER
    //
    //=============================================================================================================================
    Render(gl: WebGL2RenderingContext) {
        //=========================
        // 			UPDATE
        //=========================
        const newSubSceneStateThisFrameCached = this.bNewSubSceneStateThisFrame;
        {
            if (this.bNewSubSceneStateThisFrame) {
                TransitionToNextSubSceneState(gl, this);
                this.bNewSubSceneStateThisFrame = false;
            }

            if (this.CurSceneState != ESubSceneState.Phone) {
                OnSubSceneStateTransitionUpdate(gl, this);
                OnScriptUpdateMeshesForCurSceneState(gl, this);

                LightSwitchOnUpdateAdditional(this);
            } else {
                OnPhoneSceneUpdate(gl, this);
            }

            //Update Meshes. !TODO Remove for static ones
            for (const meshRef of this.AllMeshesArr) {
                meshRef.RegenerateMatrix();
            }

            this.SwitchRope.OnUpdate(this);
            if (this.CurSceneState == ESubSceneState.Phone) {
                if (this.MeshPills.bCanDraw) {
                    this.MeshPills.Position.Set(this.SwitchRope.RopeHandlePoint.PositionCur);
                    this.MeshPills.Position.y -= 0.18;
                    this.MeshPills.Position.y -= this.MeshPills.Scale.z * 0.5;
                    this.MeshPills.Position.z -= 0.1;
                    this.MeshPills.Orientation.z = Math.max(-1.8, this.MeshPills.Orientation.z - GTime.Delta * 0.4);

                    {
                        const p1 = this.SwitchRope.Rope.Points.at(this.SwitchRope.Rope.Points.length - 2)!.PositionCur;
                        const p0 = this.SwitchRope.RopeHandlePoint.PositionCur;
                        const handleDir = GStack.TempVec3_2;
                        handleDir.Set(p0);
                        handleDir.Negate(p1);
                        handleDir.Normalize();

                        const down = GStack.TempVec3_3;
                        down.Set3(0.0, -1.0, 0.0);
                        const dotRes = handleDir.Dot(down);
                        const angle = Math.acos(dotRes);
                        this.MeshPills.Orientation.y = -angle;
                    }
                }
            }

            super.OnUpdate(gl);
        }

        //=========================
        // 		VECTOR FIELD
        //=========================
        if (this.bRenderProjectorToDensity) {
            InjectProjectorTextureToDensity(gl, this);
        }
        if (this.bRenderColorToDensity) {
            //this.RenderToDensity(gl);
        }
        //GVectorFieldInjector.GInstance!.InjectVelocityFromUserInput(gl, this.VectorFieldProjector);

        DensityRenderEventUpdate(gl, this);

        if (this.CurSceneState !== ESubSceneState.Phone) {
            this.VectorFieldProjector.UpdateMain(gl);
        }

        /* if (this.CurSceneState == ESubSceneState.Phone) {
            SceneBase.GpCurProjectorTexture = this.ProjectorTexture.InnerTexture;
        } else {
            SceneBase.GpCurProjectorTexture = GVectorField.GInstance!.GetCurTextureDensity();
        } */
        SceneBase.GpCurProjectorTexture = this.VectorFieldProjector.GetCurTextureDensity();

        //=========================
        // 		PARTICLES UPDATE
        //=========================
        {
            this.DustParticles.Update(gl);
        }

        //=============================
        // 		BASEPASS RENDER
        //=============================

        //
        //===RENDER TO LIGHTPROBES
        //
        if (GSettings.bEnaleLightprobes) {
            SceneBase.LightprobesManager.UpdateProbes(gl, this);
        }

        //Prepare SDF Mesh
        {
            if (this.pCurSDFMesh.bCanDraw) {
                const curSDFMesh = this.pCurSDFMesh;
                SDFRender.GpSDFMeshTexture = curSDFMesh.SDFTexture3D;
                const ub = SceneBase.UniformBuffer.Params;
                ub.SDFMeshBoundsCenter.Set(curSDFMesh.Position);
                ub.SDFMeshBoundsExtent.Set(curSDFMesh.InitBBoxExtentNorm!);
                ub.SDFMeshBoundsExtent.Mul(curSDFMesh.Scale.x);
                ub.SDFMeshUniformScale.x = curSDFMesh.Scale.x;
            }
            if (this.pCurSDFMesh2 && this.pCurSDFMesh2.bCanDraw) {
                const curSDFMesh = this.pCurSDFMesh2;
                SDFRender.GpSDFMeshTexture2 = curSDFMesh.SDFTexture3D;
                const ub = SceneBase.UniformBuffer.Params;
                ub.SDFMeshBoundsCenter2.Set(curSDFMesh.Position);
                ub.SDFMeshBoundsExtent2.Set(curSDFMesh.InitBBoxExtentNorm!);
                ub.SDFMeshBoundsExtent2.Mul(curSDFMesh.Scale.x);
                ub.SDFMeshUniformScale2.x = curSDFMesh.Scale.x;
            }
        }

        //Render To Scene Color
        BindRenderTarget(gl, GRenderTargets.SceneColorFramebuffer!, GScreenDesc.RenderTargetSize, true, true);

        {
            const basePass = GBasePass.GInstance!;
            basePass.Begin(gl, this.ShaderBasePassCurScene!);
            //Render Meshes
            for (const meshRef of this.AllMeshesArr) {
                basePass.RenderMesh(gl, this.ShaderBasePassCurScene!, meshRef, SceneBase.TextureEnvMapWithMips, 1, 0);
            }
            //Debug lightprobes
            if (GSettings.bDebugLightprobes && GSettings.bEnaleLightprobes) {
                SceneBase.LightprobesManager.RenderDebugVisualizeProbes(gl, this.ShaderBasePassCurScene!);
            }
            basePass.End(gl);
        }

        this.SwitchRope.Render(gl, this);
        /* if (this.CurSceneState != ESubSceneState.Phone) {
            this.SwitchRope.Render(gl);
        }  */

        //SDF
        if (0) {
            gl.enable(gl.BLEND);
            gl.blendFunc(gl.ONE, gl.ONE);
            gl.blendEquation(gl.FUNC_ADD);

            //this.SDFSceneRender.Render(gl);

            /* BindRenderTarget(gl, GRenderTargets.SpotlightFramebuffer!, GScreenDesc.RenderTargetSize, true, false);
            this.SDFSceneRender.RenderVolumeLight(gl); */

            gl.disable(gl.BLEND);
        }

        //Wings
        if (this.bRenderWings && this.CurSceneState == ESubSceneState.Phone) {
            {
                this.MeshWingLeft.RegenerateMatrix();
                this.MeshWingRight.RegenerateMatrix();
            }
            const basePass = GBasePass.GInstance!;
            basePass.Begin(gl, this.ShaderBasePassAlpha!);
            gl.enable(gl.BLEND);
            gl.blendFunc(gl.ONE, gl.ONE);
            gl.blendEquation(gl.FUNC_ADD);
            BindRenderTarget(gl, GRenderTargets.SpotlightFramebuffer!, GScreenDesc.RenderTargetSize, true, false);
            basePass.RenderMesh(
                gl,
                this.ShaderBasePassAlpha!,
                this.MeshWingLeft,
                SceneBase.TextureEnvMapWithMips,
                1,
                0,
            );
            basePass.RenderMesh(
                gl,
                this.ShaderBasePassAlpha!,
                this.MeshWingRight,
                SceneBase.TextureEnvMapWithMips,
                1,
                0,
            );
            gl.disable(gl.BLEND);
            basePass.End(gl);
        }

        //Fullscreen VF
        {
            //Render Phone
            if (1 && this.CurSceneState == ESubSceneState.Phone) {
                const cachedAlbedo = this.MeshPhone.Material.AlbedoTexture;
                const cachedNormal = this.MeshPhone.Material.NormalTexture;
                this.MeshPhone.Material.AlbedoTexture = this.DispersionTexture;
                this.MeshPhone.Material.NormalTexture = this.DispersionMaskTexture;
                const basePass = GBasePass.GInstance!;
                basePass.Begin(gl, this.ShaderBasePassDensity!);
                gl.enable(gl.BLEND);
                gl.blendFunc(gl.ONE, gl.ONE);
                gl.blendEquation(gl.MAX);
                //VectorField.GFullscreenInstance!.BindCurDensityRT(gl);
                VectorField.GFullscreenInstance!.BindCurDensityAlphaMRTRT(gl);
                const clearColor1 = new Float32Array([0.0, 0.0, 0.0, 0.0]);
                gl.clearBufferfv(gl.COLOR, 2, clearColor1);

                basePass.RenderMesh(
                    gl,
                    this.ShaderBasePassDensity!,
                    this.MeshPhone,
                    SceneBase.TextureEnvMapWithMips,
                    1,
                    0,
                );
                basePass.RenderMesh(
                    gl,
                    this.ShaderBasePassDensity!,
                    this.MeshPhoneHandle,
                    SceneBase.TextureEnvMapWithMips,
                    1,
                    0,
                );
                basePass.End(gl);
                gl.disable(gl.BLEND);
                gl.drawBuffers([gl.COLOR_ATTACHMENT0]);

                this.MeshPhone.Material.AlbedoTexture = cachedAlbedo;
                this.MeshPhone.Material.NormalTexture = cachedNormal;

                //if (this.bPhonePickedUpThisFrame) {
                if (this.PhonePickupTimeParam > this.ScreamTextureRevealTime && !this.bScreamTextureRendered) {
                    VectorField.GFullscreenInstance!.BindCurDensityRT(gl);

                    const bright = 0.1;
                    GSimpleShapesRenderer.GInstance!.RenderPoint(
                        gl,
                        GetVec3(0.0, 2.0, 100.0),
                        GetVec3(1.6 * 50.0, 50.0, 0),
                        GetVec3(bright, bright, bright),
                        false,
                        undefined,
                        this.ScreamTexture,
                    );

                    const audioContext = GAudioEngine.Instance!.Context;
                    if (audioContext) {
                        this.SoundAh.Play(audioContext);
                    }

                    this.bScreamTextureRendered = true;
                }
            }

            //GSimpleShapesRenderer.GInstance!.RenderPoint(gl, GetVec3(0, 2.0, 0), 1.0, GetVec3(1.0, 1.0, 1.0), true);
            /* GVectorFieldInjector.GInstance!.InjectDensitySceneColor(
                gl,
                VectorField.GFullscreenInstance!,
                GetVec2(0, 0.225),
                GetVec2(0.425, 0.2),
            ); */
            /* GVectorFieldInjector.GInstance!.InjectDensityTextured(
                gl,
                VectorField.GFullscreenInstance!,
                GetVec2(0, 0),
                GetVec2(0.4, 0.4),
                this.DispersionTexture!,
                GetVec3(1.0, 0.7, 0.7),
            ); */
        }

        //GSimpleShapesRenderer.GInstance?.RenderPoint(gl, GetVec3(0, 0, this.MeshPhone.Position.z), 0.1);

        //======================
        // 		SMOKE RENDER
        //======================
        if (this.CurSceneState == ESubSceneState.Phone) {
            if (newSubSceneStateThisFrameCached) {
                BindRenderTarget(gl, GRenderTargets.SmokeFramebuffer!, GScreenDesc.HalfResRenderTargetSize, true);
            }
            BindRenderTarget(gl, GRenderTargets.SceneColorFramebuffer!, GScreenDesc.RenderTargetSize, false, false);
            this.DustParticles.Desc.Color.Set3(0.01, 0.25, 0.01);
        } else {
            BindRenderTarget(gl, GRenderTargets.SmokeFramebuffer!, GScreenDesc.HalfResRenderTargetSize, true);
        }

        this.DustParticles.Render(gl, gl.FUNC_ADD, gl.ONE, gl.ONE);
    }
}
